import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback
} from 'react';

import { MDBDataTable } from 'mdbreact';
import { Form, Col, Row, Modal } from 'react-bootstrap';
import { Button } from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import Swal from 'sweetalert2';

import { RiDeleteBinFill } from 'react-icons/ri';
import { FaEdit, FaEye } from 'react-icons/fa';
import { FaDownload, FaUpload } from 'react-icons/fa';
import { RiFileExcel2Fill } from 'react-icons/ri';

import ReactSwitch from 'react-switch';
import { PuffLoader } from 'react-spinners';

import { read, utils, writeFile } from 'xlsx';

import '../../assets/css/custom.css';

import { baseURL } from '../base_url';
import axios from 'axios';
import axiosInstance from '../../utils/axiosConfig';


/* ============================================================
   CONSTANTS
============================================================ */

const ALL_OPTION = {
  label: 'All',
  value: '0'
};

const FLEET_STATUS_ITEMS = [
  {
    label: 'Without Sold',
    value: 'all'
  },
  {
    label: 'Sold Delivered',
    value: 'Sold Delivered'
  }
];

const STELLANTIS_STATUS_ITEMS = [
  {
    label: 'All',
    value: '0'
  },
  {
    label: 'activated',
    value: 'activated'
  },
  {
    label: 'created',
    value: 'created'
  },
  {
    label: 'Rejected',
    value: 'Rejected'
  },
  {
    label: 'Not Sync',
    value: 'Not Sync'
  }
];

const FUEL_TYPE_ITEMS = [
  {
    label: 'Diesel',
    value: 'Diesel'
  },
  {
    label: 'Petrol',
    value: 'Petrol'
  },
  {
    label: 'Electric',
    value: 'Electric'
  },
  {
    label: 'LPG/Compressed Gas',
    value: 'LPG'
  }
];

const CURRENT_STATUS_ITEMS = [
  {
    label: 'Available',
    value: 'Available'
  },
  {
    label: 'In Maintenance',
    value: 'In Maintenance'
  },
  {
    label: 'Non-Active',
    value: 'Non-Active'
  },
  {
    label: 'Missing',
    value: 'Missing'
  }
];

const TELEMATIC_STATUS_ITEMS = [
  {
    label: 'Active',
    value: 'Active'
  },
  {
    label: 'In Active',
    value: 'In Active'
  }
];

const PURCHASE_TYPE_ITEMS = [
  {
    label: 'New',
    value: 'New'
  },
  {
    label: 'Used',
    value: 'Used'
  },
  {
    label: 'Lease',
    value: 'Lease'
  },
  {
    label: 'Risk',
    value: 'Risk'
  }
];

const AGREEMENT_TYPE_ITEMS = [
  {
    label: 'In Agreement',
    value: 'In Agreement'
  },
  {
    label: 'Off Agreement',
    value: 'Off Agreement'
  }
];

const MILE_LOCATIONS = new Set([
  'Denver',
  'Chicago',
  'Orlando',
  'Atlanta'
]);


/* ============================================================
   HELPERS
============================================================ */

const getStoredUser = () => {
  try {
    const token = sessionStorage.getItem('tm_token');

    if (!token) {
      return null;
    }

    return JSON.parse(token);
  } catch (error) {
    console.error('Unable to parse tm_token:', error);
    return null;
  }
};


const getTodayStart = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};


const getTodayEnd = () => {
  const date = new Date();
  date.setHours(23, 59, 59, 999);
  return date;
};


const formatDateForAPI = (date) => {
  if (!date) {
    return '';
  }

  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toISOString();
};


const sanitizeValue = (
  value,
  regex,
  maxLength,
  invalidMessage,
  maxLengthMessage,
  required = false,
  requiredMessage = ''
) => {
  let error = '';

  if (regex.test(value)) {
    error = invalidMessage;
  }

  let sanitized = value.replace(regex, '');

  if (sanitized.length > maxLength) {
    sanitized = sanitized.slice(0, maxLength);
    error = maxLengthMessage;
  }

  if (required && sanitized.trim() === '') {
    error = requiredMessage;
  }

  return {
    value: sanitized,
    error
  };
};


/* ============================================================
   COMPONENT
============================================================ */

const Fleet = () => {

  /* ==========================================================
     USER
  ========================================================== */

  const foundUser = useMemo(() => getStoredUser(), []);

  const adminId = foundUser?.tm_id;

  const fleetAccess =
    foundUser?.tm_accessRole?.Fleet || {};

  const canAdd = fleetAccess.Add === true;
  const canEdit = fleetAccess.Edit === true;
  const canDelete = fleetAccess.Delete === true;
  const canExport = fleetAccess.Export === true;


  /* ==========================================================
     REFS
  ========================================================== */

  const fileInputRef = useRef(null);


  /* ==========================================================
     GENERAL STATE
  ========================================================== */

  const [loading, setLoading] = useState(false);
  const [excelUploadCount, setExcelUploadCount] = useState('');

  const [displayForm, setDisplayForm] = useState(false);

  const [isEdited, setIsEdited] = useState('');

  const [viewEditButton, setViewEditButton] = useState(true);


  /* ==========================================================
     FILTER STATE
  ========================================================== */

  const [companyItems, setCompanyItems] = useState([]);
  const [companyItems2, setCompanyItems2] = useState([]);

  const [locationItems, setLocationItems] = useState([]);
  const [locationItems2, setLocationItems2] = useState([]);

  const [fleetMake, setFleetMake] = useState([]);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCompany2, setSelectedCompany2] = useState(null);

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedLocation2, setSelectedLocation2] = useState(null);

  const [selectedStellantisStatus, setSelectedStellantisStatus] =
    useState(STELLANTIS_STATUS_ITEMS[0]);

  const [selectedFleetMake, setSelectedFleetMake] =
    useState(ALL_OPTION);

  const [selectedFleetStatus, setSelectedFleetStatus] =
    useState(FLEET_STATUS_ITEMS[0]);


  /* ==========================================================
     FORM STATE
  ========================================================== */

  const [note, setNote] = useState('');

  const [selectedPurchaseType, setSelectedPurchaseType] =
    useState(null);

  const [selectedFuelType, setSelectedFuelType] =
    useState(null);

  const [selectedTelematicStatus, setSelectedTelematicStatus] =
    useState(null);

  const [selectedCurrentStatus, setSelectedCurrentStatus] =
    useState(null);

  const [selectedAgreementType, setSelectedAgreementType] =
    useState(null);

  const [plateExpireDate, setPlateExpireDate] =
    useState(null);

  const [registerDate, setRegisterDate] =
    useState(null);

  const [nextServiceDate, setNextServiceDate] =
    useState(null);

  const [vinNumber, setVinNumber] = useState('');

  const [sippCode, setSippCode] = useState('');

  const [agreementNumber, setAgreementNumber] =
    useState('');

  const [make, setMake] = useState('');

  const [mYear, setMYear] = useState('');

  const [model, setModel] = useState('');

  const [plate, setPlate] = useState('');

  const [state, setState] = useState('');

  const [color, setColor] = useState('');

  const [serviceKms, setServiceKms] = useState('');

  const [isActive, setIsActive] = useState(false);


  /* ==========================================================
     VALIDATION STATE
  ========================================================== */

  const initialErrors = {
    vinNumber: '',
    plate: '',
    state: '',
    mYear: '',
    make: '',
    model: '',
    sippCode: '',
    color: '',
    plateExpiry: '',
    registerDate: '',
    serviceExpiry: '',
    serviceKms: '',
    agreementNumber: ''
  };

  const [errors, setErrors] = useState(initialErrors);


  /* ==========================================================
     TABLE STATE
  ========================================================== */

  const [accessDetails, setAccessDetails] = useState([]);

  const [exportTableData, setExportTableData] =
    useState([]);


  /* ==========================================================
     MODAL
  ========================================================== */

  const [showModal, setShowModal] = useState(false);

  const handleShow = useCallback(() => {
    setShowModal(true);
  }, []);

  const handleClose = useCallback(() => {
    setShowModal(false);
  }, []);


  /* ==========================================================
     CONVERSION
  ========================================================== */

  const convertToKm = useCallback(
    (value, selectedUnit) => {
      if (
        selectedUnit === 'Miles' &&
        value !== null &&
        value !== undefined &&
        value !== ''
      ) {
        return (parseFloat(value) * 1.60934).toFixed(2);
      }

      return value;
    },
    []
  );


  /* ==========================================================
     LOAD INITIAL FILTER DATA
  ========================================================== */

  const loadInitialData = useCallback(async () => {

    if (!adminId) {
      return;
    }

    try {

      setLoading(true);

      const [
        companyResponse,
        locationResponse,
        makeResponse
      ] = await Promise.all([
        axios.get(
          `${baseURL}/get_company_setup?status=active&admin_id=${adminId}`
        ),

        axios.get(
          `${baseURL}/get_location_setup?status=active&admin_id=${adminId}`
        ),

        axios.get(
          `${baseURL}/get_all_make?status=active&admin_id=${adminId}`
        )
      ]);


      /* Companies */

      const companies = (
        companyResponse.data?.data || []
      ).map(item => ({
        label: item.company_name,
        value: item._id
      }));


      setCompanyItems([
        ALL_OPTION,
        ...companies
      ]);

      setCompanyItems2(companies);


      /* Locations */

      const locations = (
        locationResponse.data?.data || []
      ).map(item => ({
        label: item.location_name,
        value: item._id
      }));


      setLocationItems([
        ALL_OPTION,
        ...locations
      ]);


      /* Makes */

      const makes = (
        makeResponse.data?.data || []
      ).map(item => ({
        label: item,
        value: item
      }));


      setFleetMake([
        ALL_OPTION,
        ...makes
      ]);

    } catch (error) {

      console.error(
        'Error loading fleet filters:',
        error
      );

      Swal.fire({
        icon: 'error',
        text: 'Failed to load fleet filters.'
      });

    } finally {

      setLoading(false);
    }

  }, [adminId]);


  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);


  /* ==========================================================
     INITIAL DEFAULT FILTERS
  ========================================================== */

  useEffect(() => {

    if (companyItems.length > 0) {
      setSelectedCompany(companyItems[0]);
    }

    if (locationItems.length > 0) {
      setSelectedLocation(locationItems[0]);
    }

    if (fleetMake.length > 0) {
      setSelectedFleetMake(fleetMake[0]);
    }

    setSelectedStellantisStatus(
      STELLANTIS_STATUS_ITEMS[0]
    );

    setSelectedFleetStatus(
      FLEET_STATUS_ITEMS[0]
    );

  }, [
    companyItems,
    locationItems,
    fleetMake
  ]);


  /* ==========================================================
     LOAD FLEET DATA
  ========================================================== */

  const homeTableData = useCallback(
    async (
      comId = '0',
      locId = '0',
      statusId = '0',
      makeName = '0',
      fleetStatus = 'all'
    ) => {

      if (!adminId) {
        return;
      }

      try {

        setLoading(true);

        const response = await axiosInstance.get(
          `${baseURL}/get_fleet_setup`,
          {
            params: {
              com_id: comId,
              loc_id: locId,
              status_id: statusId,
              make: makeName,
              fleet_status: fleetStatus,
              admin_id: adminId
            }
          }
        );


        if (response.data?.status !== '1') {

          setAccessDetails([]);
          setExportTableData([]);

          return;
        }


        const fleetData =
          response.data?.data || [];


        const rowsData = fleetData.map(
          (val, index) => {

            const selectedUnit =
              MILE_LOCATIONS.has(val.loc_name)
                ? 'Miles'
                : '';


            return {

              id: index + 1,

            //   com_name:
            //     val.com_name || '',

              location:
                val.loc_name || '',

              vin:
                val.vin_number || '',

              make:
                val.make || '',

              model:
                val.model || '',

              plate:
                val.plate || '',


              agree_status: (
                <div className="text-center">
                  <label>
                    {val.agreement_type || ''}
                  </label>
                </div>
              ),


              agree_number:
                val.agreement_number || '',


              current_kilometers:
                val.current_kilometers
                  ? `${convertToKm(
                      val.current_kilometers,
                      selectedUnit
                    )} km`
                  : '',

            last_api_data:
                val.last_api_data || '',
              telematic_status: (
                <div className="text-center">

                  <ReactSwitch
                    height={20}
                    checked={
                      val.telematic_status ===
                      'Active'
                    }
                    onChange={() =>
                      handleAgreeTeleChange(
                        `tele-${val._id}`,
                        val.telematic_status
                      )
                    }
                  />

                  <br />

                  <label>
                    {val.telematic_status || ''}
                  </label>

                </div>
              ),


              created_by:
                val.c_name || '',

              status:
                val.current_status || '',

              ridesiq_status:
                val.ridesiq_status || '',


              action: (
                <React.Fragment>

                  <FaEye
                    title="View"
                    onClick={() =>
                      vieweditHandle(
                        `view-${val._id}`
                      )
                    }
                    style={{
                      cursor: 'pointer'
                    }}
                  />


                  {canEdit && (
                    <>
                      {' | '}

                      <FaEdit
                        title="Edit"
                        onClick={() =>
                          vieweditHandle(
                            `edit-${val._id}`
                          )
                        }
                        style={{
                          cursor: 'pointer'
                        }}
                      />
                    </>
                  )}


                  {canDelete && (
                    <>
                      {' | '}

                      <RiDeleteBinFill
                        title="Delete"
                        onClick={() =>
                          deleteHandle(val._id)
                        }
                        style={{
                          cursor: 'pointer'
                        }}
                      />
                    </>
                  )}

                </React.Fragment>
              )

            };
          }
        );


        const exportData =
          fleetData.map((val, index) => {

            const selectedUnit =
              MILE_LOCATIONS.has(val.loc_name)
                ? 'Miles'
                : '';

            const odometer =
              val.current_kilometers
                ? `${convertToKm(
                    val.current_kilometers,
                    selectedUnit
                  )} km`
                : '';


            return [
              index + 1,
            //   val.com_name || '',
              val.loc_name || '',
              val.vin_number || '',
              val.make || '',
              val.model || '',
              val.plate || '',
              val.agreement_type || '',
              val.agreement_number || '',
              odometer,
              val.last_api_data,
              val.current_status || '',
              val.ridesiq_status || '',
              val.c_name || ''
            ];
          });


        setAccessDetails(rowsData);

        setExportTableData(exportData);

      } catch (error) {

        console.error(
          'Error loading fleet:',
          error
        );

        setAccessDetails([]);
        setExportTableData([]);

        Swal.fire({
          icon: 'error',
          text: 'Failed to load fleet data.'
        });

      } finally {

        setLoading(false);
      }

    },
    [
      adminId,
      canEdit,
      canDelete,
      convertToKm
    ]
  );


  /* ==========================================================
     LOAD TABLE AFTER FILTER DATA AVAILABLE
  ========================================================== */

  const initialFleetLoaded = useRef(false);

  useEffect(() => {

    if (
      companyItems.length === 0 ||
      locationItems.length === 0 ||
      fleetMake.length === 0
    ) {
      return;
    }

    if (initialFleetLoaded.current) {
      return;
    }

    initialFleetLoaded.current = true;

    homeTableData(
      '0',
      '0',
      '0',
      '0',
      'all'
    );

  }, [
    companyItems,
    locationItems,
    fleetMake,
    homeTableData
  ]);


  /* ==========================================================
     REFRESH CURRENT TABLE
  ========================================================== */

  const refreshFleetTable = useCallback(() => {

    homeTableData(
      selectedCompany?.value || '0',
      selectedLocation?.value || '0',
      selectedStellantisStatus?.value || '0',
      selectedFleetMake?.value || '0',
      selectedFleetStatus?.value || 'all'
    );

  }, [
    homeTableData,
    selectedCompany,
    selectedLocation,
    selectedStellantisStatus,
    selectedFleetMake,
    selectedFleetStatus
  ]);


  /* ==========================================================
     COMPANY FILTER
  ========================================================== */

  const handleCompanyChange = useCallback(
    async (selectedOption) => {

      if (!selectedOption) {
        return;
      }

      setSelectedCompany(selectedOption);

      setSelectedStellantisStatus(
        STELLANTIS_STATUS_ITEMS[0]
      );

      setSelectedFleetMake(
        ALL_OPTION
      );

      try {

        setLoading(true);

        const response = await axios.get(
          `${baseURL}/get_location_setup`,
          {
            params: {
              status: 'active',
              com_id: selectedOption.value,
              admin_id: adminId
            }
          }
        );


        const locations = (
          response.data?.data || []
        ).map(item => ({
          label: item.location_name,
          value: item._id
        }));


        const options = [
          ALL_OPTION,
          ...locations
        ];


        setLocationItems(options);

        setSelectedLocation(
          ALL_OPTION
        );


        await homeTableData(
          selectedOption.value,
          '0',
          '0',
          '0',
          'all'
        );

      } catch (error) {

        console.error(
          'Error loading company locations:',
          error
        );

        Swal.fire({
          icon: 'error',
          text: 'Failed to load locations.'
        });

      } finally {

        setLoading(false);
      }

    },
    [
      adminId,
      homeTableData
    ]
  );


  /* ==========================================================
     LOCATION FILTER
  ========================================================== */

  const handleLocationChange = useCallback(
    async (selectedOption) => {

      if (!selectedOption) {
        return;
      }

      setSelectedLocation(
        selectedOption
      );

      setSelectedStellantisStatus(
        STELLANTIS_STATUS_ITEMS[0]
      );

      setSelectedFleetMake(
        ALL_OPTION
      );

      await homeTableData(
        selectedCompany?.value || '0',
        selectedOption.value,
        '0',
        '0',
        'all'
      );

    },
    [
      selectedCompany,
      homeTableData
    ]
  );


  /* ==========================================================
     MAKE FILTER
  ========================================================== */

  const handleFleetMake = useCallback(
    async (selectedOption) => {

      if (!selectedOption) {
        return;
      }

      setSelectedFleetMake(
        selectedOption
      );

      await homeTableData(
        selectedCompany?.value || '0',
        selectedLocation?.value || '0',
        selectedStellantisStatus?.value || '0',
        selectedOption.value,
        selectedFleetStatus?.value || 'all'
      );

    },
    [
      selectedCompany,
      selectedLocation,
      selectedStellantisStatus,
      selectedFleetStatus,
      homeTableData
    ]
  );


  /* ==========================================================
     FLEET STATUS FILTER
  ========================================================== */

  const handleFleetStatus = useCallback(
    async (selectedOption) => {

      if (!selectedOption) {
        return;
      }

      setSelectedFleetStatus(
        selectedOption
      );

      await homeTableData(
        selectedCompany?.value || '0',
        selectedLocation?.value || '0',
        selectedStellantisStatus?.value || '0',
        selectedFleetMake?.value || '0',
        selectedOption.value
      );

    },
    [
      selectedCompany,
      selectedLocation,
      selectedStellantisStatus,
      selectedFleetMake,
      homeTableData
    ]
  );


  /* ==========================================================
     STELLANTIS STATUS
  ========================================================== */

  const handleStellantisStatus =
    useCallback(
      async (selectedOption) => {

        if (!selectedOption) {
          return;
        }

        setSelectedStellantisStatus(
          selectedOption
        );

        setSelectedFleetMake(
          ALL_OPTION
        );

        await homeTableData(
          selectedCompany?.value || '0',
          selectedLocation?.value || '0',
          selectedOption.value,
          '0',
          'all'
        );

      },
      [
        selectedCompany,
        selectedLocation,
        homeTableData
      ]
    );


  /* ==========================================================
     RESET FILTERS
  ========================================================== */

  const handleReset = useCallback(
    async () => {

      setSelectedCompany(
        companyItems[0] || ALL_OPTION
      );

      setSelectedLocation(
        locationItems[0] || ALL_OPTION
      );

      setSelectedStellantisStatus(
        STELLANTIS_STATUS_ITEMS[0]
      );

      setSelectedFleetMake(
        fleetMake[0] || ALL_OPTION
      );

      setSelectedFleetStatus(
        FLEET_STATUS_ITEMS[0]
      );


      await homeTableData(
        '0',
        '0',
        '0',
        '0',
        'all'
      );

    },
    [
      companyItems,
      locationItems,
      fleetMake,
      homeTableData
    ]
  );


  /* ==========================================================
     FORM RESET
  ========================================================== */

  const resetForm = useCallback(() => {

    setSelectedCompany2(null);
    setSelectedLocation2(null);

    setLocationItems2([]);

    setVinNumber('');
    setSippCode('');
    setAgreementNumber('');
    setMake('');
    setMYear('');
    setModel('');
    setPlate('');
    setState('');
    setPlateExpireDate(null);
    setRegisterDate(null);
    setColor('');
    setSelectedCurrentStatus(null);
    setSelectedTelematicStatus(null);
    setSelectedAgreementType(null);
    setNextServiceDate(null);
    setServiceKms('');
    setSelectedFuelType(null);
    setSelectedPurchaseType(null);
    setNote('');
    setIsActive(false);

    setErrors(initialErrors);

    setIsEdited('');
    setViewEditButton(true);

  }, []);


  /* ==========================================================
     ADD FLEET
  ========================================================== */

  const handleSubmit = useCallback(() => {

    resetForm();

    setDisplayForm(true);

    setViewEditButton(true);

    setIsEdited('');

  }, [resetForm]);


  /* ==========================================================
     COMPANY CHANGE - FORM
  ========================================================== */

  const handleCompanyChange2 =
    useCallback(
      async (selectedOption) => {

        if (!selectedOption) {
          return;
        }

        setSelectedCompany2(
          selectedOption
        );

        try {

          setLoading(true);

          const response = await axios.get(
            `${baseURL}/get_location_setup`,
            {
              params: {
                status: 'active',
                com_id: selectedOption.value,
                admin_id: adminId
              }
            }
          );


          const locations = (
            response.data?.data || []
          ).map(item => ({
            label: item.location_name,
            value: item._id
          }));


          setLocationItems2(
            locations
          );

          setSelectedLocation2(null);

        } catch (error) {

          console.error(
            'Error loading locations:',
            error
          );

          Swal.fire({
            icon: 'error',
            text: 'Failed to load locations.'
          });

        } finally {

          setLoading(false);
        }

      },
      [adminId]
    );


  /* ==========================================================
     LOAD EDIT LOCATION
  ========================================================== */

  const handleCompanyChangeEdit =
    useCallback(
      async (companyId, locationId) => {

        try {

          const response = await axios.get(
            `${baseURL}/get_location_setup`,
            {
              params: {
                status: 'active',
                com_id: companyId,
                admin_id: adminId
              }
            }
          );


          const locations = (
            response.data?.data || []
          ).map(item => ({
            label: item.location_name,
            value: item._id
          }));


          setLocationItems2(
            locations
          );


          const selected =
            locations.find(
              option =>
                String(option.value) ===
                String(locationId)
            );


          setSelectedLocation2(
            selected || null
          );

        } catch (error) {

          console.error(
            'Error loading edit locations:',
            error
          );

        }

      },
      [adminId]
    );


  /* ==========================================================
     LOCATION CHANGE - FORM
  ========================================================== */

  const handleLocationChange2 =
    useCallback(
      selectedOption => {

        setSelectedLocation2(
          selectedOption || null
        );

      },
      []
    );


  /* ==========================================================
     GENERIC SELECT HANDLERS
  ========================================================== */

  const handlePurchaseType = useCallback(
    selectedOption => {
      setSelectedPurchaseType(
        selectedOption || null
      );
    },
    []
  );


  const handleTelematicStatus =
    useCallback(
      selectedOption => {
        setSelectedTelematicStatus(
          selectedOption || null
        );
      },
      []
    );


  const handleCurrentStatus = useCallback(
    selectedOption => {
      setSelectedCurrentStatus(
        selectedOption || null
      );
    },
    []
  );


  const handleFuelType = useCallback(
    selectedOption => {
      setSelectedFuelType(
        selectedOption || null
      );
    },
    []
  );


  const handleAgreementType =
    useCallback(
      selectedOption => {
        setSelectedAgreementType(
          selectedOption || null
        );
      },
      []
    );


  /* ==========================================================
     VIEW / EDIT FLEET
  ========================================================== */

  const vieweditHandle = useCallback(
    async id => {

      const [, rowId] = id.split('-');

      if (!rowId) {
        return;
      }

      try {

        setLoading(true);

        const response = await axios.post(
          `${baseURL}/get_fleet_single`,
          {
            id: rowId
          }
        );


        if (response.data?.status !== '1') {

          Swal.fire({
            icon: 'error',
            text: 'Something went wrong.'
          });

          return;
        }


        const fleet = response.data.data;


        setDisplayForm(true);


        /* Company */

        const company =
          companyItems2.find(
            option =>
              String(option.value) ===
              String(fleet.company_id)
          );


        setSelectedCompany2(
          company || null
        );


        /* Location */

        await handleCompanyChangeEdit(
          fleet.company_id,
          fleet.location_id
        );


        /* Basic fields */

        setVinNumber(
          fleet.vin_number || ''
        );

        setSippCode(
          fleet.sipp_code || ''
        );

        setAgreementNumber(
          fleet.agreement_number || ''
        );

        setMake(
          fleet.make || ''
        );

        setMYear(
          fleet.make_year || ''
        );

        setModel(
          fleet.model || ''
        );

        setPlate(
          fleet.plate || ''
        );

        setState(
          fleet.plate_state || ''
        );

        setColor(
          fleet.color || ''
        );

        setServiceKms(
          fleet.service_kms || ''
        );

        setNote(
          fleet.note || ''
        );


        /* Dates */

        setPlateExpireDate(
          fleet.plate_expire
            ? new Date(fleet.plate_expire)
            : null
        );

        setRegisterDate(
          fleet.registration_date
            ? new Date(fleet.registration_date)
            : null
        );

        setNextServiceDate(
          fleet.next_service_date
            ? new Date(fleet.next_service_date)
            : null
        );


        /* Selects */

        setSelectedCurrentStatus(
          CURRENT_STATUS_ITEMS.find(
            option =>
              option.value ===
              fleet.current_status
          ) || null
        );


        setSelectedTelematicStatus(
          TELEMATIC_STATUS_ITEMS.find(
            option =>
              option.value ===
              fleet.telematic_status
          ) || null
        );


        /* FIXED:
           Previously setFleetMake() was used here
           and compared the option value to the
           entire fleet object.
        */

        setSelectedFleetMake(
          fleetMake.find(
            option =>
              option.value === fleet.make
          ) || ALL_OPTION
        );


        setSelectedAgreementType(
          AGREEMENT_TYPE_ITEMS.find(
            option =>
              option.value ===
              fleet.agreement_type
          ) || null
        );


        setSelectedFuelType(
          FUEL_TYPE_ITEMS.find(
            option =>
              option.value ===
              fleet.fuel_type
          ) || null
        );


        setSelectedPurchaseType(
          PURCHASE_TYPE_ITEMS.find(
            option =>
              option.value ===
              fleet.purchase_type
          ) || null
        );


        setIsActive(
          fleet.is_active === 'True' ||
          fleet.is_active === true
        );


        if (id.startsWith('view-')) {

          setViewEditButton(false);

          setIsEdited('');

        } else {

          setViewEditButton(true);

          setIsEdited(
            fleet._id
          );

        }

      } catch (error) {

        console.error(
          'Error loading fleet:',
          error
        );

        Swal.fire({
          icon: 'error',
          text: 'Failed to load fleet data.'
        });

      } finally {

        setLoading(false);
      }

    },
    [
      companyItems2,
      fleetMake,
      handleCompanyChangeEdit
    ]
  );


  /* ==========================================================
     VALIDATE FORM
  ========================================================== */

  const validateForm = useCallback(() => {

    const validationErrors = {};


    if (!selectedCompany2?.value) {
      return {
        valid: false,
        message: 'Please Select Company.'
      };
    }


    if (!selectedLocation2?.value) {
      return {
        valid: false,
        message: 'Please Select Location.'
      };
    }


    if (!vinNumber.trim()) {
      return {
        valid: false,
        message: 'Please Enter VIN Number.'
      };
    }


    if (vinNumber.length !== 17) {
      return {
        valid: false,
        message: 'VIN Number must be 17 characters.'
      };
    }


    if (!make.trim()) {
      return {
        valid: false,
        message: 'Please Enter Make.'
      };
    }


    if (!mYear.trim()) {
      return {
        valid: false,
        message: 'Please Enter Make Year.'
      };
    }


    if (mYear.length !== 4) {
      return {
        valid: false,
        message: 'Make Year must be 4 digits.'
      };
    }


    if (!model.trim()) {
      return {
        valid: false,
        message: 'Please Enter Model.'
      };
    }


    if (!plate.trim()) {
      return {
        valid: false,
        message: 'Please Enter Plate Number.'
      };
    }


    if (!state.trim()) {
      return {
        valid: false,
        message: 'Please Enter Plate State.'
      };
    }


    if (!selectedTelematicStatus?.value) {
      return {
        valid: false,
        message: 'Please Select Telematic Status.'
      };
    }


    if (!selectedAgreementType?.value) {
      return {
        valid: false,
        message: 'Please Select Agreement Type.'
      };
    }


    return {
      valid: true,
      errors: validationErrors
    };

  }, [
    selectedCompany2,
    selectedLocation2,
    vinNumber,
    make,
    mYear,
    model,
    plate,
    state,
    selectedTelematicStatus,
    selectedAgreementType
  ]);


  /* ==========================================================
     SUBMIT FLEET FORM
  ========================================================== */

  const handleSubmitForm = useCallback(
    async e => {

      e.preventDefault();


      const validation =
        validateForm();


      if (!validation.valid) {

        Swal.fire({
          icon: 'error',
          text: validation.message
        });

        return;
      }


      try {

        setLoading(true);


        const payload = {

          admin_id: adminId,

          company_id:
            selectedCompany2.value,

          location_id:
            selectedLocation2.value,

          vin_number:
            vinNumber.trim(),

          sipp_code:
            sippCode.trim(),

          agreement_number:
            agreementNumber.trim(),

          make:
            make.trim(),

          make_year:
            mYear.trim(),

          model:
            model.trim(),

          plate:
            plate.trim(),

          plate_state:
            state.trim(),

          plate_expire:
            formatDateForAPI(
              plateExpireDate
            ),

          registration_date:
            formatDateForAPI(
              registerDate
            ),

          color:
            color.trim(),

          current_status:
            selectedCurrentStatus?.value || '',

          telematic_status:
            selectedTelematicStatus.value,

          agreement_type:
            selectedAgreementType.value,

          next_service_date:
            formatDateForAPI(
              nextServiceDate
            ),

          service_kms:
            serviceKms.trim(),

          fuel_type:
            selectedFuelType?.value || '',

          purchase_type:
            selectedPurchaseType?.value || '',

          note:
            note.trim(),

          status:
            isActive,

          is_edited:
            isEdited

        };


        const response = await axios.post(
          `${baseURL}/create_fleet`,
          payload
        );


        const status =
          response.data?.status;


        if (status === 'success') {

          setDisplayForm(false);

          await Swal.fire({
            icon: 'success',
            text:
              `${vinNumber} fleet created successfully.`
          });


          await homeTableData(
            '0',
            '0',
            '0',
            '0',
            'all'
          );

        }

        else if (status === 'duplicate') {

          await Swal.fire({
            icon: 'error',
            html:
              `<b>${vinNumber}</b> already exists. Please try with a different fleet number.`
          });

        }

        else if (status === 'updated') {

          setDisplayForm(false);

          await Swal.fire({
            icon: 'success',
            text:
              `${vinNumber} fleet updated successfully.`
          });


          await homeTableData(
            '0',
            '0',
            '0',
            '0',
            'all'
          );

        }

        else if (status === 'rejected') {

          await Swal.fire({
            icon: 'error',
            text:
              'The VIN is Not eligible.'
          });

        }

        else {

          await Swal.fire({
            icon: 'error',
            text:
              'Something went wrong.'
          });

        }

      } catch (error) {

        console.error(
          'Error saving fleet:',
          error
        );

        Swal.fire({
          icon: 'error',
          text:
            error.response?.data?.message ||
            'Failed to save fleet.'
        });

      } finally {

        setLoading(false);
      }

    },
    [
      adminId,
      selectedCompany2,
      selectedLocation2,
      vinNumber,
      sippCode,
      agreementNumber,
      make,
      mYear,
      model,
      plate,
      state,
      plateExpireDate,
      registerDate,
      color,
      selectedCurrentStatus,
      selectedTelematicStatus,
      selectedAgreementType,
      nextServiceDate,
      serviceKms,
      selectedFuelType,
      selectedPurchaseType,
      note,
      isActive,
      isEdited,
      validateForm,
      homeTableData
    ]
  );


  /* ==========================================================
     CANCEL FORM
  ========================================================== */

  const cancelForm = useCallback(e => {

    e.preventDefault();

    setDisplayForm(false);

  }, []);


  /* ==========================================================
     TELE / AGREEMENT STATUS UPDATE
  ========================================================== */

  const handleAgreeTeleChange =
    useCallback(
      async (id, val) => {

        const result =
          await Swal.fire({
            title:
              'Are you sure you want to switch?',
            showCancelButton: true,
            confirmButtonText: 'Yes'
          });


        if (!result.isConfirmed) {
          return;
        }


        const [type, rowId] =
          id.split('-');


        let agreeVal =
          'Off Agreement';

        let teleVal =
          'In Active';


        if (type === 'agree') {

          agreeVal =
            val === 'Off Agreement'
              ? 'In Agreement'
              : 'Off Agreement';

        }


        if (type === 'tele') {

          teleVal =
            val === 'In Active'
              ? 'Active'
              : 'In Active';

        }


        try {

          setLoading(true);


          const response =
            await axios.post(
              `${baseURL}/fleet_update_status`,
              {
                id: rowId,
                admin_id: adminId,
                agree_val: agreeVal,
                tele_val: teleVal,
                type
              }
            );


          if (
            response.data?.status === '1'
          ) {

            await Swal.fire(
              'Updated!',
              'Status has been updated.',
              'success'
            );


            await refreshFleetTable();

          } else {

            await Swal.fire({
              icon: 'error',
              text:
                'Something went wrong.'
            });

          }

        } catch (error) {

          console.error(
            'Error updating fleet status:',
            error
          );

          Swal.fire({
            icon: 'error',
            text:
              'Failed to update status.'
          });

        } finally {

          setLoading(false);
        }

      },
      [
        adminId,
        refreshFleetTable
      ]
    );


  /* ==========================================================
     DELETE
  ========================================================== */

  const deleteHandle = useCallback(
    async id => {

      const result =
        await Swal.fire({
          title:
            'Are you sure you want to delete?',
          showCancelButton: true,
          confirmButtonText: 'Yes'
        });


      if (!result.isConfirmed) {
        return;
      }


      try {

        setLoading(true);


        const response =
          await axios.post(
            `${baseURL}/fleet_delete`,
            {
              id,
              admin_id: adminId
            }
          );


        if (
          response.data?.status === '1'
        ) {

          await refreshFleetTable();


          await Swal.fire(
            'Deleted!',
            'Fleet has been deleted.',
            'success'
          );

        } else {

          await Swal.fire({
            icon: 'error',
            text:
              'Something went wrong.'
          });

        }

      } catch (error) {

        console.error(
          'Error deleting fleet:',
          error
        );

        Swal.fire({
          icon: 'error',
          text:
            'Failed to delete fleet.'
        });

      } finally {

        setLoading(false);
      }

    },
    [
      adminId,
      refreshFleetTable
    ]
  );


  /* ==========================================================
     EXCEL TEMPLATE DOWNLOAD
  ========================================================== */

  const handleExport = useCallback(() => {

    const headings = [[
      'VIN',
      'Year',
      'Make',
      'Model',
      'Plate',
      'Plate State',
      'Current Location',
      'Status Description',
      'Current R/A',
      'Current Kilometers'
    ]];


    const sampleVin = [
      [
        '1HGCM82633A123456',
        '2020',
        'DODGE',
        'DURANGO',
        'A00301',
        'AB',
        'YYC',
        'On Rent',
        'ATL-1234',
        '10000'
      ],
      [
        '19UYA31581L000000',
        '2020',
        'DODGE',
        'DURANGO',
        'A00301',
        'AB',
        'YYC',
        'AVAILABLE',
        '',
        '15000'
      ]
    ];


    const wb = utils.book_new();

    const ws = utils.aoa_to_sheet(
      headings
    );


    utils.sheet_add_aoa(
      ws,
      sampleVin,
      {
        origin: 'A2'
      }
    );


    ws['!cols'] = [
      { width: 22 },
      { width: 10 },
      { width: 15 },
      { width: 20 },
      { width: 15 },
      { width: 15 },
      { width: 20 },
      { width: 25 },
      { width: 20 },
      { width: 20 }
    ];


    utils.book_append_sheet(
      wb,
      ws,
      'Fleet'
    );


    writeFile(
      wb,
      'Fleet Upload.xlsx'
    );

  }, []);


  /* ==========================================================
     TABLE EXPORT
  ========================================================== */

  const handleExportTableData =
    useCallback(() => {

      const headings = [[
        'S.No',
        // 'Company Name',
        'Location',
        'VIN Number',
        'Make',
        'Model',
        'Plate',
        'Agreement Status',
        'Agreement Number',
        'TSD Odometer',
        'Last API Data',
        'TSD Status',
        'RidesIQ Status',
        'Updated By'
      ]];


      const wb = utils.book_new();

      const ws = utils.aoa_to_sheet(
        headings
      );


      if (exportTableData.length > 0) {

        utils.sheet_add_aoa(
          ws,
          exportTableData,
          {
            origin: 'A2'
          }
        );

      }


      utils.book_append_sheet(
        wb,
        ws,
        'Fleet List'
      );


      writeFile(
        wb,
        'Fleet List.xlsx'
      );

    }, [
      exportTableData
    ]);


  /* ==========================================================
     EXCEL IMPORT
  ========================================================== */

  const handleImport = useCallback(
    async event => {

      const files =
        event.target.files;


      if (!files || !files.length) {
        return;
      }


      const file = files[0];


      try {

        setLoading(true);

        setExcelUploadCount(
          'Uploading...'
        );


        /* ------------------------------------------------------
           Upload file to server
        ------------------------------------------------------ */

        const formData =
          new FormData();


        formData.append(
          'file',
          file
        );

        formData.append(
          'admin_id',
          adminId
        );


        await axios.post(
          `${baseURL}/save_uploaded_file`,
          formData,
          {
            headers: {
              'Content-Type':
                'multipart/form-data'
            }
          }
        );


        /* ------------------------------------------------------
           Read Excel
        ------------------------------------------------------ */

        const buffer =
          await file.arrayBuffer();


        const workbook =
          read(buffer);


        const sheets =
          workbook.SheetNames;


        if (!sheets.length) {

          throw new Error(
            'Excel file contains no sheets.'
          );

        }


        const worksheet =
          workbook.Sheets[
            sheets[0]
          ];


        const rows =
          utils.sheet_to_json(
            worksheet,
            {
              header: 1,
              defval: ''
            }
          );


        const requiredHeaders = [
          'VIN',
          'Year',
          'Make',
          'Model',
          'Plate',
          'Plate State',
          'Current Location',
          'Status Description',
          'Current R/A',
          'Current Kilometers'
        ];


        const headers =
          rows[0] || [];


        const validHeaders =
          requiredHeaders.every(
            header =>
              headers.includes(header)
          );


        if (!validHeaders) {

          setExcelUploadCount('');

          Swal.fire({
            icon: 'error',
            text:
              'You have uploaded an invalid file. Please download the sample template for your reference.'
          });

          return;
        }


        const data =
          utils.sheet_to_json(
            worksheet,
            {
              defval: ''
            }
          );


        /* ------------------------------------------------------
           Empty file
        ------------------------------------------------------ */

        if (!data.length) {

          setExcelUploadCount('');

          Swal.fire({
            icon: 'error',
            text:
              'You have uploaded an empty file.'
          });

          return;
        }


        /* ------------------------------------------------------
           Maximum records
        ------------------------------------------------------ */

        if (data.length > 4000) {

          setExcelUploadCount('');

          Swal.fire({
            icon: 'error',
            text:
              `Oops! Your file size is ${data.length}, but the maximum allowed size is 4000. Please reduce your file size and try again.`
          });

          return;
        }


        /* ------------------------------------------------------
           Upload fleet data
        ------------------------------------------------------ */

        await axios.post(
          `${baseURL}/upload_fleet_excel`,
          {
            data:
              JSON.stringify(data),

            admin_id:
              adminId
          }
        );


        /* ------------------------------------------------------
           Update count
        ------------------------------------------------------ */

        await axios.post(
          `${baseURL}/update_fleet_record_count`,
          {
            fleet_count:
              data.length,

            admin_id:
              adminId
          }
        );


        setExcelUploadCount('');


        await Swal.fire({
          icon: 'success',
          text:
            'Fleets have been updated successfully.'
        });


        /* ------------------------------------------------------
           Refresh table instead of page reload
        ------------------------------------------------------ */

        await refreshFleetTable();


      } catch (error) {

        console.error(
          'Error uploading fleet Excel:',
          error
        );


        setExcelUploadCount('');


        Swal.fire({
          icon: 'error',
          text:
            error.response?.data?.message ||
            'Failed to upload data.'
        });

      } finally {

        setLoading(false);

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

      }

    },
    [
      adminId,
      refreshFleetTable
    ]
  );


  /* ==========================================================
     SYNC STELLANTIS
  ========================================================== */

  const handleSyncStellantisSelected =
    useCallback(
      async value => {

        handleClose();


        try {

          setLoading(true);


          await axios.get(
            `${baseURL}/sync_stellantis`,
            {
              params: {
                selected_sync:
                  value,

                admin_id:
                  adminId
              }
            }
          );


          await refreshFleetTable();


          await Swal.fire({
            icon: 'success',
            text:
              'Data Synced successfully.'
          });

        } catch (error) {

          console.error(
            'Error syncing Stellantis:',
            error
          );

          Swal.fire({
            icon: 'error',
            text:
              'Failed to sync data.'
          });

        } finally {

          setLoading(false);
        }

      },
      [
        adminId,
        handleClose,
        refreshFleetTable
      ]
    );


  /* ==========================================================
     TABLE COLUMNS
  ========================================================== */

  const tableColumns = useMemo(
    () => [
      {
        label: 'S.No',
        field: 'id'
      },
    //   {
    //     label: 'Company Name',
    //     field: 'com_name'
    //   },
      {
        label: 'Location',
        field: 'location'
      },
      {
        label: 'VIN Number',
        field: 'vin'
      },
      {
        label: 'Make',
        field: 'make'
      },
      {
        label: 'Model',
        field: 'model'
      },
      {
        label: 'Plate',
        field: 'plate'
      },
      {
        label: 'Agreement Status',
        field: 'agree_status'
      },
      {
        label: 'Agreement Number',
        field: 'agree_number'
      },
      {
        label: 'TSD Odometer',
        field: 'current_kilometers'
      },
      {
        label: 'Last API Data',
        field: 'last_api_data'
      },
      {
        label: 'Updated By',
        field: 'created_by'
      },
      {
        label: 'TSD Status',
        field: 'status'
      },
      {
        label: 'RidesIQ Status',
        field: 'ridesiq_status'
      },
      {
        label: 'Action',
        field: 'action'
      }
    ],
    []
  );


  const tableData = useMemo(
    () => ({
      columns: tableColumns,
      rows: accessDetails
    }),
    [
      tableColumns,
      accessDetails
    ]
  );


  const searchOptions = useMemo(
    () => ({
      text: 'Search by name',
      showSearch: true,
      searchLabel: 'Search:'
    }),
    []
  );


  /* ==========================================================
     FORM FIELD HANDLERS
  ========================================================== */

  const handleVinChange = useCallback(
    e => {

      const value =
        e.target.value;


      const result =
        sanitizeValue(
          value,
          /[^A-Za-z0-9\-]/g,
          17,
          'VIN can only contain letters, numbers, and dash.',
          'VIN cannot exceed 17 characters.',
          true,
          'VIN is required.'
        );


      setVinNumber(
        result.value
      );


      setErrors(prev => ({
        ...prev,
        vinNumber:
          result.error
      }));

    },
    []
  );


  const handlePlateChange = useCallback(
    e => {

      const value =
        e.target.value;


      const result =
        sanitizeValue(
          value,
          /[^A-Za-z0-9\-]/g,
          10,
          'Plate should contain letters, numbers, or dash only.',
          'Plate cannot exceed 10 characters.',
          true,
          'Plate is required.'
        );


      setPlate(
        result.value
      );


      setErrors(prev => ({
        ...prev,
        plate:
          result.error
      }));

    },
    []
  );


  const handleStateChange = useCallback(
    e => {

      const value =
        e.target.value;


      const result =
        sanitizeValue(
          value,
          /[^A-Za-z]/g,
          3,
          'State should contain letters only.',
          'State cannot exceed 3 characters.',
          true,
          'State is required.'
        );


      setState(
        result.value
      );


      setErrors(prev => ({
        ...prev,
        state:
          result.error
      }));

    },
    []
  );


  const handleYearChange = useCallback(
    e => {

      const value =
        e.target.value;


      const result =
        sanitizeValue(
          value,
          /[^0-9]/g,
          4,
          'Year should contain digits only.',
          'Year cannot exceed 4 digits.',
          true,
          'Year is required.'
        );


      setMYear(
        result.value
      );


      setErrors(prev => ({
        ...prev,
        mYear:
          result.error
      }));

    },
    []
  );


  const handleMakeChange = useCallback(
    e => {

      const value =
        e.target.value;


      const result =
        sanitizeValue(
          value,
          /[^A-Za-z ]/g,
          20,
          'Make should contain letters only.',
          'Make cannot exceed 20 characters.',
          true,
          'Make is required.'
        );


      setMake(
        result.value
      );


      setErrors(prev => ({
        ...prev,
        make:
          result.error
      }));

    },
    []
  );


  const handleModelChange = useCallback(
    e => {

      const value =
        e.target.value;


      const result =
        sanitizeValue(
          value,
          /[^A-Za-z *]/g,
          40,
          'Model can only contain letters, spaces and *.',
          'Model cannot exceed 40 characters.',
          true,
          'Model is required.'
        );


      setModel(
        result.value
      );


      setErrors(prev => ({
        ...prev,
        model:
          result.error
      }));

    },
    []
  );


  const handleSippCodeChange =
    useCallback(e => {

      const value =
        e.target.value;


      const result =
        sanitizeValue(
          value,
          /[^A-Za-z0-9]/g,
          4,
          'SIPP Code can only contain letters and numbers.',
          'SIPP Code cannot exceed 4 characters.'
        );


      setSippCode(
        result.value
      );


      setErrors(prev => ({
        ...prev,
        sippCode:
          result.error
      }));

    }, []);


  const handleColorChange =
    useCallback(e => {

      const value =
        e.target.value;


      const result =
        sanitizeValue(
          value,
          /[^A-Za-z ]/g,
          20,
          'Color can only contain letters and spaces.',
          'Color cannot exceed 20 characters.'
        );


      setColor(
        result.value
      );


      setErrors(prev => ({
        ...prev,
        color:
          result.error
      }));

    }, []);


  const handleServiceKmsChange =
    useCallback(e => {

      const value =
        e.target.value;


      const result =
        sanitizeValue(
          value,
          /[^0-9]/g,
          6,
          'Service Kms can only contain digits.',
          'Service Kms cannot exceed 6 digits.'
        );


      setServiceKms(
        result.value
      );


      setErrors(prev => ({
        ...prev,
        serviceKms:
          result.error
      }));

    }, []);


  const handleAgreementNumberChange =
    useCallback(e => {

      const value =
        e.target.value;


      const result =
        sanitizeValue(
          value,
          /[^A-Za-z0-9\-]/g,
          20,
          'Agreement Number can only contain letters, digits, and dash.',
          'Agreement Number cannot exceed 20 characters.'
        );


      setAgreementNumber(
        result.value
      );


      setErrors(prev => ({
        ...prev,
        agreementNumber:
          result.error
      }));

    }, []);


  /* ==========================================================
     DATE HANDLERS
  ========================================================== */

  const handlePlateExpiryChange =
    useCallback(date => {

      let error = '';


      if (
        date &&
        date < getTodayStart()
      ) {
        error =
          'Plate expiry date cannot be in the past.';
      }


      setPlateExpireDate(date);


      setErrors(prev => ({
        ...prev,
        plateExpiry:
          error
      }));

    }, []);


  const handleRegistrationDateChange =
    useCallback(date => {

      let error = '';


      if (
        date &&
        date > getTodayEnd()
      ) {
        error =
          'Registration date cannot be in the future.';
      }


      setRegisterDate(date);


      setErrors(prev => ({
        ...prev,
        registerDate:
          error
      }));

    }, []);


  const handleServiceDateChange =
    useCallback(date => {

      let error = '';


      if (
        date &&
        date < getTodayStart()
      ) {
        error =
          'Service Date cannot be in the past.';
      }


      setNextServiceDate(
        date
      );


      setErrors(prev => ({
        ...prev,
        serviceExpiry:
          error
      }));

    }, []);


  /* ==========================================================
     MODAL COMPONENT
  ========================================================== */

  const MyModal = ({
    showModal,
    handleClose
  }) => {

    return (
      <Modal
        size="lg"
        show={showModal}
        onHide={handleClose}
      >

        <Modal.Header closeButton>
          <Modal.Title>
            Choose Sync Type
          </Modal.Title>
        </Modal.Header>


        <Modal.Body>

          <Button
            variant="contained"
            onClick={() =>
              handleSyncStellantisSelected(
                'new'
              )
            }
            style={{
              marginLeft: '2rem'
            }}
          >
            Sync New
          </Button>


          <Button
            variant="contained"
            onClick={() =>
              handleSyncStellantisSelected(
                'Rejected'
              )
            }
            style={{
              marginLeft: '2rem'
            }}
          >
            Sync Rejected
          </Button>


          <Button
            variant="contained"
            onClick={() =>
              handleSyncStellantisSelected(
                'created'
              )
            }
            style={{
              marginLeft: '2rem'
            }}
          >
            Sync Created
          </Button>


          <Button
            variant="contained"
            onClick={() =>
              handleSyncStellantisSelected(
                'activated'
              )
            }
            style={{
              marginLeft: '2rem'
            }}
          >
            Sync Activated
          </Button>


          <br />
          <br />

          <div className="d-flex text-center">
            <i>
              **Ford and Nissan Vehicles can not be sync**
            </i>
          </div>

        </Modal.Body>

      </Modal>
    );
  };


  /* ==========================================================
     RENDER
  ========================================================== */

  return (
    <div className="body_class">

      {/* ======================================================
          LOADER
      ====================================================== */}

      {loading && (
        <div className="loader-overlay">

          <div className="loader">

            <div className="excel-upload-count">
              {excelUploadCount}
            </div>

            <PuffLoader
              color="#00308F"
              loading={true}
              size={200}
            />

          </div>

        </div>
      )}


      <div className="container-fluid">


        {/* ====================================================
            FLEET LIST / FILTERS
        ==================================================== */}

        {!displayForm && (
          <>

            <div className="mt-3">

              <Form.Group
                as={Row}
                controlId="fleetFilters"
              >

                {/* Company */}

                <Form.Label column sm="1">
                  Company*
                </Form.Label>

                <Col sm="2">

                  <Select
                    placeholder="Select Company"
                    options={companyItems}
                    onChange={
                      handleCompanyChange
                    }
                    value={
                      selectedCompany
                    }
                  />

                </Col>


                {/* Location */}

                <Form.Label column sm="1">
                  Location*
                </Form.Label>

                <Col sm="2">

                  <Select
                    placeholder="Select Location"
                    options={locationItems}
                    onChange={
                      handleLocationChange
                    }
                    value={
                      selectedLocation
                    }
                  />

                </Col>


                {/* Make */}

                <Form.Label column sm="1">
                  Make*
                </Form.Label>

                <Col sm="2">

                  <Select
                    placeholder="Select Make"
                    options={fleetMake}
                    onChange={
                      handleFleetMake
                    }
                    value={
                      selectedFleetMake
                    }
                  />

                </Col>


                {/* Fleet Status */}

                <Form.Label column sm="1">
                  Status
                </Form.Label>

                <Col sm="2">

                  <Select
                    placeholder="Select Status"
                    options={
                      FLEET_STATUS_ITEMS
                    }
                    onChange={
                      handleFleetStatus
                    }
                    value={
                      selectedFleetStatus
                    }
                  />

                </Col>

              </Form.Group>


              {/* Buttons */}

              <Button
                size="large"
                className="mt-4"
                variant="contained"
                onClick={
                  handleReset
                }
              >
                Reset
              </Button>


              {canAdd && (
                <Button
                  size="large"
                  className="mt-4"
                  style={{
                    marginLeft: '2rem'
                  }}
                  variant="contained"
                  onClick={
                    handleSubmit
                  }
                >
                  + Add Fleet
                </Button>
              )}


              {canEdit && (
                <Button
                  size="large"
                  className="mt-4"
                  style={{
                    marginLeft: '2rem'
                  }}
                  variant="contained"
                  onClick={
                    handleExport
                  }
                >
                  <FaDownload
                    size={18}
                  />
                  &nbsp;
                  Download Template
                </Button>
              )}


              {canEdit && (
                <>
                  <input
                    type="file"
                    id="select-csv"
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    style={{
                      display: 'none'
                    }}
                    multiple={false}
                    onChange={
                      handleImport
                    }
                    ref={
                      fileInputRef
                    }
                  />

                  <label htmlFor="select-csv">

                    <Button
                      size="large"
                      variant="contained"
                      style={{
                        marginLeft: '2rem'
                      }}
                      className="btn btn-primary mt-4"
                      component="span"
                    >
                      <FaUpload
                        size={18}
                      />
                      &nbsp;
                      Upload Excel
                    </Button>

                  </label>
                </>
              )}

            </div>


            {/* =================================================
                TABLE
            ================================================= */}

            <div className="row mt-1">

              <div className="col-md-12">

                {canExport && (
                  <Button
                    size="small"
                    style={{
                      marginBottom: '-3.5rem'
                    }}
                    variant="contained"
                    onClick={
                      handleExportTableData
                    }
                  >
                    <RiFileExcel2Fill
                      size={16}
                    />
                    &nbsp;
                    Export
                  </Button>
                )}


                <MDBDataTable
                  bordered
                  data={tableData}
                  large={true}
                  displayEntries={false}
                  search={
                    searchOptions
                  }
                  noBottomColumns={true}
                  className="pagination-right-aligned"
                  searching={true}
                />

              </div>

            </div>

          </>
        )}


        {/* ====================================================
            ADD / EDIT FORM
        ==================================================== */}

        {displayForm && (
          <>

            <h4>
              {isEdited
                ? 'Edit Fleet'
                : 'Add Fleet'}
            </h4>


            <div className="mt-4">

              <div className="container-fluid">

                <Form
                  onSubmit={
                    handleSubmitForm
                  }
                >


                  {/* ==========================================
                      MANDATORY
                  ========================================== */}

                  <div className="p-3 madatory_fields">

                    <div className="form_section">
                      **Mandatory Fields**
                    </div>


                    {/* Company / Location */}

                    <Form.Group
                      as={Row}
                      className="mb-4"
                    >

                      <Form.Label
                        column
                        sm="2"
                      >
                        Company Name *
                      </Form.Label>

                      <Col sm="4">

                        <Select
                          placeholder="Select Company"
                          options={
                            companyItems2
                          }
                          onChange={
                            handleCompanyChange2
                          }
                          value={
                            selectedCompany2
                          }
                          isDisabled={
                            !viewEditButton
                          }
                        />

                      </Col>


                      <Form.Label
                        column
                        sm="2"
                      >
                        Location Name *
                      </Form.Label>

                      <Col sm="4">

                        <Select
                          placeholder="Select Location"
                          options={
                            locationItems2
                          }
                          onChange={
                            handleLocationChange2
                          }
                          value={
                            selectedLocation2
                          }
                          isDisabled={
                            !viewEditButton
                          }
                        />

                      </Col>

                    </Form.Group>


                    {/* VIN / Plate */}

                    <Form.Group
                      as={Row}
                      className="mb-3"
                    >

                      <Form.Label
                        column
                        sm="2"
                      >
                        VIN Number *
                      </Form.Label>


                      <Col sm="4">

                        <Form.Control
                          type="text"
                          value={
                            vinNumber
                          }
                          placeholder="Please Enter VIN Number"
                          onChange={
                            handleVinChange
                          }
                          disabled={
                            !viewEditButton
                          }
                        />

                        {errors.vinNumber && (
                          <small className="text-danger d-block mt-1">
                            {
                              errors.vinNumber
                            }
                          </small>
                        )}

                      </Col>


                      <Form.Label
                        column
                        sm="2"
                      >
                        Plate / State *
                      </Form.Label>


                      <Col
                        sm="4"
                        className="d-flex"
                        style={{
                          gap: '0.5rem'
                        }}
                      >

                        <div
                          style={{
                            flex: 1
                          }}
                        >

                          <Form.Control
                            type="text"
                            value={
                              plate
                            }
                            placeholder="Please Enter Plate Number"
                            onChange={
                              handlePlateChange
                            }
                            disabled={
                              !viewEditButton
                            }
                          />

                          {errors.plate && (
                            <small className="text-danger d-block mt-1">
                              {
                                errors.plate
                              }
                            </small>
                          )}

                        </div>


                        <div
                          style={{
                            width: '30%'
                          }}
                        >

                          <Form.Control
                            type="text"
                            value={
                              state
                            }
                            placeholder="State"
                            onChange={
                              handleStateChange
                            }
                            disabled={
                              !viewEditButton
                            }
                          />

                          {errors.state && (
                            <small className="text-danger d-block mt-1">
                              {
                                errors.state
                              }
                            </small>
                          )}

                        </div>

                      </Col>

                    </Form.Group>


                    {/* Year / Make / Model */}

                    <Form.Group
                      as={Row}
                      className="mb-3"
                    >

                      <Form.Label
                        column
                        sm="2"
                      >
                        Year / Make *
                      </Form.Label>


                      <Col
                        sm="4"
                        className="d-flex"
                        style={{
                          gap: '0.5rem'
                        }}
                      >

                        <div
                          style={{
                            width: '30%'
                          }}
                        >

                          <Form.Control
                            type="text"
                            value={
                              mYear
                            }
                            placeholder="Year"
                            onChange={
                              handleYearChange
                            }
                            disabled={
                              !viewEditButton
                            }
                          />

                          {errors.mYear && (
                            <small className="text-danger d-block mt-1">
                              {
                                errors.mYear
                              }
                            </small>
                          )}

                        </div>


                        <div
                          style={{
                            flex: 1
                          }}
                        >

                          <Form.Control
                            type="text"
                            value={
                              make
                            }
                            placeholder="Please Enter Make"
                            onChange={
                              handleMakeChange
                            }
                            disabled={
                              !viewEditButton
                            }
                          />

                          {errors.make && (
                            <small className="text-danger d-block mt-1">
                              {
                                errors.make
                              }
                            </small>
                          )}

                        </div>

                      </Col>


                      <Form.Label
                        column
                        sm="2"
                      >
                        Model *
                      </Form.Label>


                      <Col sm="4">

                        <Form.Control
                          type="text"
                          value={
                            model
                          }
                          placeholder="Please Enter Model"
                          onChange={
                            handleModelChange
                          }
                          disabled={
                            !viewEditButton
                          }
                        />

                        {errors.model && (
                          <small className="text-danger d-block mt-1">
                            {
                              errors.model
                            }
                          </small>
                        )}

                      </Col>

                    </Form.Group>


                    {/* Agreement / Telematic */}

                    <Form.Group
                      as={Row}
                      className="mb-3"
                    >

                      <Form.Label
                        column
                        sm="2"
                      >
                        Agreement Type *
                      </Form.Label>

                      <Col sm="4">

                        <Select
                          placeholder="Select Agreement Type"
                          options={
                            AGREEMENT_TYPE_ITEMS
                          }
                          onChange={
                            handleAgreementType
                          }
                          value={
                            selectedAgreementType
                          }
                          isDisabled={
                            !viewEditButton
                          }
                        />

                      </Col>


                      <Form.Label
                        column
                        sm="2"
                      >
                        Telematic Status *
                      </Form.Label>

                      <Col sm="4">

                        <Select
                          placeholder="Select Telematic Status"
                          options={
                            TELEMATIC_STATUS_ITEMS
                          }
                          onChange={
                            handleTelematicStatus
                          }
                          value={
                            selectedTelematicStatus
                          }
                          isDisabled={
                            !viewEditButton
                          }
                        />

                      </Col>

                    </Form.Group>

                  </div>


                  {/* ==========================================
                      NON MANDATORY
                  ========================================== */}

                  <div className="p-3 madatory_fields">

                    <div className="form_section">
                      **Non Mandatory Fields**
                    </div>


                    {/* SIPP / Color */}

                    <Form.Group
                      as={Row}
                      className="mb-3"
                    >

                      <Form.Label
                        column
                        sm="2"
                      >
                        SIPP / Class Code
                      </Form.Label>

                      <Col sm="4">

                        <Form.Control
                          type="text"
                          value={
                            sippCode
                          }
                          placeholder="Please Enter SIPP / Class Code"
                          onChange={
                            handleSippCodeChange
                          }
                          disabled={
                            !viewEditButton
                          }
                        />

                        {errors.sippCode && (
                          <small className="text-danger d-block mt-1">
                            {
                              errors.sippCode
                            }
                          </small>
                        )}

                      </Col>


                      <Form.Label
                        column
                        sm="2"
                      >
                        Color
                      </Form.Label>

                      <Col sm="4">

                        <Form.Control
                          type="text"
                          value={
                            color
                          }
                          placeholder="Please Enter Color"
                          onChange={
                            handleColorChange
                          }
                          disabled={
                            !viewEditButton
                          }
                        />

                        {errors.color && (
                          <small className="text-danger d-block mt-1">
                            {
                              errors.color
                            }
                          </small>
                        )}

                      </Col>

                    </Form.Group>


                    {/* Dates */}

                    <Form.Group
                      as={Row}
                      className="mb-3"
                    >

                      <Form.Label
                        column
                        sm="2"
                      >
                        Plate Expires
                      </Form.Label>


                      <Col sm="4">

                        <DatePicker
                          selected={
                            plateExpireDate
                          }
                          onChange={
                            handlePlateExpiryChange
                          }
                          dateFormat="MM/dd/yyyy"
                          placeholderText="Select Plate Expires Date"
                          className="form-control"
                          minDate={
                            new Date()
                          }
                          disabled={
                            !viewEditButton
                          }
                        />

                        {errors.plateExpiry && (
                          <small className="text-danger d-block mt-1">
                            {
                              errors.plateExpiry
                            }
                          </small>
                        )}

                      </Col>


                      <Form.Label
                        column
                        sm="2"
                      >
                        Registration Date
                      </Form.Label>


                      <Col sm="4">

                        <DatePicker
                          selected={
                            registerDate
                          }
                          onChange={
                            handleRegistrationDateChange
                          }
                          dateFormat="MM/dd/yyyy"
                          placeholderText="Select Registration Date"
                          className="form-control"
                          maxDate={
                            new Date()
                          }
                          disabled={
                            !viewEditButton
                          }
                        />

                        {errors.registerDate && (
                          <small className="text-danger d-block mt-1">
                            {
                              errors.registerDate
                            }
                          </small>
                        )}

                      </Col>

                    </Form.Group>


                    {/* Service Date / Service Kms */}

                    <Form.Group
                      as={Row}
                      className="mb-3"
                    >

                      <Form.Label
                        column
                        sm="2"
                      >
                        Next Service Date
                      </Form.Label>


                      <Col sm="4">

                        <DatePicker
                          selected={
                            nextServiceDate
                          }
                          onChange={
                            handleServiceDateChange
                          }
                          dateFormat="MM/dd/yyyy"
                          placeholderText="Select Next Service Date"
                          className="form-control"
                          minDate={
                            new Date()
                          }
                          disabled={
                            !viewEditButton
                          }
                        />

                        {errors.serviceExpiry && (
                          <small className="text-danger d-block mt-1">
                            {
                              errors.serviceExpiry
                            }
                          </small>
                        )}

                      </Col>


                      <Form.Label
                        column
                        sm="2"
                      >
                        Service Kms
                      </Form.Label>


                      <Col sm="4">

                        <Form.Control
                          type="text"
                          value={
                            serviceKms
                          }
                          placeholder="Please Enter Service Kms"
                          onChange={
                            handleServiceKmsChange
                          }
                          disabled={
                            !viewEditButton
                          }
                        />

                        {errors.serviceKms && (
                          <small className="text-danger d-block mt-1">
                            {
                              errors.serviceKms
                            }
                          </small>
                        )}

                      </Col>

                    </Form.Group>


                    {/* Fuel / Current Status */}

                    <Form.Group
                      as={Row}
                      className="mb-3"
                    >

                      <Form.Label
                        column
                        sm="2"
                      >
                        Fuel Type
                      </Form.Label>


                      <Col sm="4">

                        <Select
                          placeholder="Select Fuel Type"
                          options={
                            FUEL_TYPE_ITEMS
                          }
                          onChange={
                            handleFuelType
                          }
                          value={
                            selectedFuelType
                          }
                          isDisabled={
                            !viewEditButton
                          }
                        />

                      </Col>


                      <Form.Label
                        column
                        sm="2"
                      >
                        Current Status
                      </Form.Label>


                      <Col sm="4">

                        <Select
                          placeholder="Select Current Status"
                          options={
                            CURRENT_STATUS_ITEMS
                          }
                          onChange={
                            handleCurrentStatus
                          }
                          value={
                            selectedCurrentStatus
                          }
                          isDisabled={
                            !viewEditButton
                          }
                        />

                      </Col>

                    </Form.Group>


                    {/* Agreement Number */}

                    <Form.Group
                      as={Row}
                      className="mb-3"
                    >

                      <Form.Label
                        column
                        sm="2"
                      >
                        Agreement Number
                      </Form.Label>


                      <Col sm="4">

                        <Form.Control
                          type="text"
                          value={
                            agreementNumber
                          }
                          placeholder="Please Enter Agreement Number"
                          onChange={
                            handleAgreementNumberChange
                          }
                          disabled={
                            !viewEditButton
                          }
                        />

                        {errors.agreementNumber && (
                          <small className="text-danger d-block mt-1">
                            {
                              errors.agreementNumber
                            }
                          </small>
                        )}

                      </Col>

                    </Form.Group>


                    {/* Note */}

                    <Form.Group
                      as={Row}
                      className="mb-3"
                    >

                      <Form.Label
                        column
                        sm="2"
                      >
                        Note
                      </Form.Label>


                      <Col sm="8">

                        <Form.Control
                          as="textarea"
                          value={
                            note
                          }
                          onChange={e =>
                            setNote(
                              e.target.value
                            )
                          }
                          rows={3}
                          placeholder="Note"
                          disabled={
                            !viewEditButton
                          }
                        />

                      </Col>

                    </Form.Group>

                  </div>


                  {/* ==========================================
                      ACTIONS
                  ========================================== */}

                  <div
                    style={{
                      textAlign: 'center'
                    }}
                  >

                    <Form.Group
                      as={Row}
                      className="mt-4"
                    >

                      <Col sm="5" />

                      <Col sm="2">

                        <Form.Check
                          type="checkbox"
                          id="checkbox"
                          label="Is Active"
                          checked={
                            isActive
                          }
                          onChange={e =>
                            setIsActive(
                              e.target.checked
                            )
                          }
                          disabled={
                            !viewEditButton
                          }
                        />

                      </Col>

                    </Form.Group>


                    {viewEditButton && (
                      <Button
                        variant="contained"
                        type="submit"
                      >
                        {
                          isEdited
                            ? 'Update'
                            : 'Submit'
                        }
                      </Button>
                    )}


                    <Button
                      variant="outlined"
                      type="button"
                      className="m-4"
                      onClick={
                        cancelForm
                      }
                    >
                      Cancel
                    </Button>

                  </div>

                </Form>

              </div>

            </div>

          </>
        )}

      </div>


      {/* ======================================================
          SYNC MODAL
      ====================================================== */}

      <MyModal
        showModal={
          showModal
        }
        handleClose={
          handleClose
        }
      />

    </div>
  );
};


export default Fleet;